import React from 'react';
import { FieldType, FieldOptionFetchResponse, FieldOption, Dependency } from 'types/FieldTypes';
import TextField from './fields/TextField';
import TextareaField from './fields/TextareaField';
import NumberField from './fields/NumberField';
import PasswordField from './fields/PasswordField';
import EmailField from './fields/EmailField';
import DateField from './fields/DateField';
import TabsField from './fields/TabsField';
import DropdownField from './fields/DropdownField';
import SearchSelectField from './fields/SearchSelectField';
import MultiSearchSelectField from './fields/MultiSearchSelectField';
import TimeField from './fields/TimeField';
import MultiSelectField from './fields/MultiSelectField';
import CheckboxField from './fields/CheckboxField';
import FieldHeader from './fields/elements/FieldHeader';
import RadioField from './fields/RadioField';
import TotalPriceField from './fields/TotalPriceField';
import ImageField from './fields/ImageField';
import PriceCurrencyFieldset from './mutli-fields/PriceCurrencyMultiField';
import PhoneNumbersFieldset from './fieldsets/PhoneNumbersFieldset';
import EmailAddressesFieldset from './fieldsets/EmailAddressesFieldset';
import AttachmentsFieldset from './fieldsets/AttachmentsFieldset';
import LocationsFieldset from './fieldsets/LocationsFieldset';
import ExpirationDaysField from './fields/ExpirationDaysField';
import PipelineStagesRowsField from './fieldsets/PipelineStagesRowsField';
import JobWorkflowDefaultTasksRowsField from './fieldsets/JobWorkflowDefaultTasksRowsField';
import JobWorkflowDefaultItemsRowsField from './fieldsets/JobWorkflowDefaultItemsRowsField';
import ConditionLinesDefaultsRowsField from './fieldsets/ConditionLinesDefaultsRowsField';
import WeekdayCheckboxesField from './fields/WeekdayCheckboxesField';
import SchedulingAppointmentTextField from './fieldsets/SchedulingAppointmentTextField';
import PriceField from './fields/PriceField';
import PercentageField from './fields/PercentageField';
import SchedulingBoardResourcesRowsField from './fieldsets/SchedulingBoardResourcesRowsField';
import FieldDescription from './fields/elements/FieldDescription';
import ReleaseItemsRowsField from './fieldsets/ReleaseItemsRowsField';
import TabsMultiField from './fields/TabsMultiField';
import ClientInternalNote from './fields/ClientInternalNote';
import SecretField from './fields/SecretField';
import SwitchField from './fields/SwitchField';
import AddressLookupField from './fields/AddressLookupField';

/*
 * GenerateFieldComponents.tsx
 * General function which generates the field components of a form. It is used by the InlineForm component
 * which gets the fields from certain form configuration files. This function maps these fields and its
 * properties to the right fields and return these components to the Inline Form to use in the return jsx.
 * The function also handles if fields needs to be wrapped inside a div or not which is used for styling
 * and positioning purposes on the form.
 */

export const generateFieldComponents = (
    viewKey: string,
    fields: FieldType[],
    dropdownData: Record<string, FieldOptionFetchResponse> | undefined,
    groupOptions: FieldOption[] | undefined,
    formData: any,
    updatedData: any,
    activeFeatures: string[],
    itemId?: number | null,
    onTypeChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void,
): JSX.Element[] => {
    // Handle if dropdown data or groupoptions are undefined
    dropdownData = dropdownData ?? {};
    groupOptions = groupOptions ?? [];

    // Create an empty array to store the components in the given order
    let components: JSX.Element[] = [];

    // Loop through the fields and construct the components in the right order
    fields.forEach((field, index) => {
        let FieldComponent;
        let fieldComponentsInGroup;

        // Skip rendering if the certain feature is not active
        if ('activeFeature' in field && field.activeFeature) {
            if (!activeFeatures.includes(field.activeFeature)) {
                return;
            }
        }

        // Skip rendering if showOnEdit is set and editItemId is not available
        if ('showOnEdit' in field) {
            if ((field.showOnEdit && !itemId) || (!field.showOnEdit && itemId)) {
                return;
            }
        }

        // Check if the field is dependent on another field to show
        let shouldShowField = true;
        if ('dependentOn' in field && field.dependentOn) {

            // Check if either on or more dependencies exist for this field
            const dependencies = Array.isArray(field.dependentOn) ? field.dependentOn : [field.dependentOn];

            // Loop through the dependencies
            for (const dependency of dependencies as Dependency[]) {
                const { fieldName, value, notValue } = dependency;

                let dependentFieldExists = false;
                let dependentFieldValue: any;

                // When the fieldname has a dot, handle it as nested value
                if (fieldName.includes('.')) {

                    // Helper function to get the nested value from the object
                    const getNestedValue = (obj: any, path: string) => {
                        if (!obj) return undefined;
                        return path.split('.').reduce((acc, key) => {
                            if (acc && typeof acc === 'object') {
                                return acc[key];
                            }
                            return undefined;
                        }, obj);
                    };

                    // Get the nested value from the updated data or the existing data
                    dependentFieldValue = getNestedValue(updatedData, fieldName) ?? getNestedValue(formData, fieldName);

                    // Check if the nested value exists in the updated data or form data
                    dependentFieldExists = dependentFieldValue !== undefined;

                // Handle flat field names
                } else {

                    // Check if the dependend field exists in the data or updated data
                    dependentFieldExists = (formData && fieldName in formData) || (updatedData && fieldName in updatedData);

                    // Get the field value form the existing data or updated data
                    dependentFieldValue = updatedData?.[fieldName] ?? formData?.[fieldName];
                }

                // Handle filtering on value: show the field if the value exists in the data
                if (value !== undefined) {

                    // If the dependent field doesn't exist in the data, don't show the related field
                    if (!dependentFieldExists) {
                        shouldShowField = false;
                        break
                    }
                    
                    // // Check for the most recent value between updated data and fetched data
                    // const dependentFieldValue = updatedData[fieldName] ?? formData[fieldName];

                    // Determine if the related field has to be shown
                    if (typeof value === 'boolean') {
                        // Handle boolean value dependency
                        if (dependentFieldValue !== value) {
                            shouldShowField = false;
                            break;
                        }
                    } else if (Array.isArray(value)) {
                        // If a value array is provided, check if one of its value correspondes
                        if (Array.isArray(dependentFieldValue)) {
                            if (!value.some(val => dependentFieldValue.includes(val))) {
                                shouldShowField = false;
                                break;
                            }
                        } else if (!value.includes(dependentFieldValue)) {
                            shouldShowField = false;
                            break;
                        }
                    } else {
                        // If a single value is provided
                        if (Array.isArray(dependentFieldValue)) {
                            if (!dependentFieldValue.includes(value)) {
                                shouldShowField = false;
                                break;
                            }
                        } else if (dependentFieldValue !== value) {
                            shouldShowField = false;
                            break;
                        }
                    }
                }

                // Handle filtering on not value: show the field if the value does not exist in the data
                if (notValue !== undefined) {

                    // When the field value does not exist, show the field
                    if (!dependentFieldExists) {
                        continue;
                    }

                    // When the field value does exist, don't show the field
                    if (Array.isArray(notValue)) {
                        if (notValue.includes(dependentFieldValue)) {
                            shouldShowField = false;
                            break;
                        }
                    } else {
                        if (dependentFieldValue === notValue) {
                            shouldShowField = false;
                            break;
                        }
                    }
                }
            }            
        }

        // Check if the field only may show if it's filled
        if ('showIfFilled' in field && field.showIfFilled) {
            const fieldDataExists = formData && formData[field.name] !== undefined;
            const isBooleanField = field.type === 'checkbox';
            shouldShowField = fieldDataExists && (!isBooleanField || formData[field.name] === true);
        }

        // Skip rendering if the field should not be shown
        if (!shouldShowField) {
            return;
        }

        // Pick the right component based on the field type
        switch(field.type) {
            case 'text':
                FieldComponent = <TextField {...field} viewKey={viewKey} data={formData} shouldAutoFocus={field.shouldAutoFocus ?? index === 0} />;
                break;
            case 'textarea':
                FieldComponent = <TextareaField {...field} viewKey={viewKey} data={formData} shouldAutoFocus={index === 0} />;
                break;
            case 'number':
                FieldComponent = <NumberField {...field} viewKey={viewKey} data={formData} shouldAutoFocus={index === 0} />;
                break;
            case 'percentage':
                FieldComponent = <PercentageField {...field} viewKey={viewKey} data={formData} shouldAutoFocus={index === 0} />;
                break;
            case 'expiration-days':
                FieldComponent = <ExpirationDaysField {...field} viewKey={viewKey} data={formData} shouldAutoFocus={index === 0} />;
                break;    
            case 'price':
                FieldComponent = <PriceField {...field} viewKey={viewKey} data={formData} dropdownData={dropdownData} />;
                break;
            case 'totalprice':
                FieldComponent = <TotalPriceField {...field} viewKey={viewKey} data={formData} />;
                break;
            case 'date':
                const resolvedDateField = {
                    ...field,
                    utcDatetime: resolveDatetime(field.utcDatetime, formData),
                    localDatetime: resolveDatetime(field.localDatetime, formData),
                };

                FieldComponent = <DateField {...resolvedDateField} viewKey={viewKey} data={formData} />;
                break;
            case 'time':
                const resolvedTimeField = {
                    ...field,
                    utcDatetime: resolveDatetime(field.utcDatetime, formData),
                    localDatetime: resolveDatetime(field.localDatetime, formData),
                };

                FieldComponent = <TimeField {...resolvedTimeField} viewKey={viewKey} data={formData} />
                break;
            case 'dropdown':
                FieldComponent = <DropdownField {...field} viewKey={viewKey} data={formData} dropdownData={dropdownData} />;
                break;
            case 'multiselect':
                FieldComponent = <MultiSelectField {...field} viewKey={viewKey} data={formData} dropdownData={dropdownData} groupOptions={groupOptions} />;
                break;
            case 'searchselect':
                const currentValue = String(updatedData[field.name] ?? formData?.[field.name] ?? '');

                let resolvedAddNewItem = field.addNewItem;

                if (field.addNewItem?.method === 'modal-form' && typeof field.addNewItem.modalForm === 'function') {
                    const modalFactory = field.addNewItem.modalForm as (value?: string) => React.ReactElement;

                    resolvedAddNewItem = {
                        ...field.addNewItem,
                        modalForm: modalFactory(currentValue),
                    };
                }

                FieldComponent = <SearchSelectField {...field} addNewItem={resolvedAddNewItem} viewKey={viewKey} data={formData} />
                break;
            case 'multi-searchselect':
                FieldComponent = <MultiSearchSelectField {...field} viewKey={viewKey} data={formData} />
                break;
            case 'radio':
                FieldComponent = <RadioField {...field} viewKey={viewKey} data={formData} />;
                break;
            case 'tabs':
                FieldComponent = <TabsField {...field} viewKey={viewKey} data={formData} dropdownData={dropdownData} onChange={onTypeChange}/>;
                break;
            case 'weekday-checkboxes':
                FieldComponent = <WeekdayCheckboxesField viewKey={viewKey} {...field} data={formData} />;
                break;
            case 'scheduling-appointment-text':
                FieldComponent = <SchedulingAppointmentTextField viewKey={viewKey} {...field} data={formData} />;
                break;
            case 'switch':
                FieldComponent = <SwitchField {...field} viewKey={viewKey} data={formData} />;
                break;
            case 'multi-tabs':
                FieldComponent = <TabsMultiField {...field} viewKey={viewKey} data={formData} />;
                break;
            case 'checkbox':
                FieldComponent = <CheckboxField {...field} viewKey={viewKey} data={formData}/>;
                break;
            case 'email':
                FieldComponent = <EmailField {...field} viewKey={viewKey} data={formData} shouldAutoFocus={index === 0} />;
                break;
            case 'password':
                FieldComponent = <PasswordField {...field} viewKey={viewKey} data={formData} shouldAutoFocus={index === 0} />;
                break;
            case 'secret':
                FieldComponent = <SecretField {...field} viewKey={viewKey} data={formData} shouldAutoFocus={index === 0} />;
                break;
            case 'image':
                FieldComponent = <ImageField {...field} viewKey={viewKey} data={formData} />;
                break;
            case 'separator':
                FieldComponent = <div className='separator'>—</div>
                break;
            case 'divider':
                FieldComponent = <div className='divider'></div>
                break;
            case 'header':
                FieldComponent = <FieldHeader {...field} />
                break;
            case 'description':
                FieldComponent = <FieldDescription {...field} />
                break;
            case 'phonenumbers':
                FieldComponent = <PhoneNumbersFieldset viewKey={viewKey} {...field} data={formData} shouldAutoFocus={index === 0} />;
                break;
            case 'emailaddresses':
                FieldComponent = <EmailAddressesFieldset viewKey={viewKey} {...field} data={formData} shouldAutoFocus={index === 0} />;
                break;
            case 'price-currency':
                FieldComponent = <PriceCurrencyFieldset viewKey={viewKey} {...field} data={formData} dropdownData={dropdownData} shouldAutoFocus={index === 0} />;
                break;
            case 'locations':
                FieldComponent = <LocationsFieldset viewKey={viewKey} {...field} data={formData} objectId={itemId} shouldAutoFocus={index === 0} />;
                break;
            case 'attachments':
                FieldComponent = <AttachmentsFieldset viewKey={viewKey} {...field} data={formData} />;
                break;
            case 'pipeline-stages':
                FieldComponent = <PipelineStagesRowsField viewKey={viewKey} {...field} data={formData} />;
                break;
            case 'job-workflow-default-tasks':
                FieldComponent = <JobWorkflowDefaultTasksRowsField viewKey={viewKey} {...field} data={formData} dropdownData={dropdownData} />;
                break;
            case 'job-workflow-default-items':
                FieldComponent = <JobWorkflowDefaultItemsRowsField viewKey={viewKey} {...field} data={formData} />;
                break;
            case 'condition-lines-defaults':
                FieldComponent = <ConditionLinesDefaultsRowsField viewKey={viewKey} {...field} data={formData} />;
                break;
            case 'release-items':
                FieldComponent = <ReleaseItemsRowsField viewKey={viewKey} {...field} data={formData} />;
                break;
            case 'scheduling-board-resources':
                FieldComponent = <SchedulingBoardResourcesRowsField viewKey={viewKey} {...field} data={formData} dropdownData={dropdownData} />;
                break;
            case 'client-internal-note':
                FieldComponent = <ClientInternalNote {...field} viewKey={viewKey} value={updatedData?.client?.internal_note} />;
                break;
            case 'render':
                FieldComponent = field.render?.();
                break;
            case 'address-lookup':
                // Rendered without the field wrapper, so it takes no space while idle
                components.push(<AddressLookupField key={`${field.type}_${index}`} viewKey={viewKey} />);
                return;
            default:
                FieldComponent = null;
        }

        // Render the field component
        if (FieldComponent) {
            fieldComponentsInGroup = (
                <div key={'name' in field && field.name ? `${field.name}_${index}` : `${field.type}_${index}`}
                     style={{ width: field.width || '100%', display: ('honeypotField' in field && field.honeypotField) ? 'none' : 'inline-block' }} 
                     className={`formfield ${('layout' in field && field.layout) ? field.layout : ''}`}>
                    {FieldComponent}
                </div>
            );
            // If the field has a group, wrap the components inside a div with the group name as it's class
            if (field.group) {
                // If the previous component is a group with the same name, add this component to that group
                if (components.length > 0 && components[components.length - 1].key === field.group) {
                    components[components.length - 1] = (
                        <div key={field.group} className={`${field.group}`}>
                            {components[components.length - 1].props.children}
                            {fieldComponentsInGroup}
                        </div>
                    );
                // Otherwise, create a new group
                } else {
                    components.push(
                        <div key={field.group} className={`${field.group}`}>
                            {fieldComponentsInGroup}
                        </div>
                    );
                }
            // If the field has no group, add the component directly to the array
            } else {
                components.push(fieldComponentsInGroup);
            }
        }
    });

    return components;
}


// Check wheter the given datetime value is a datetime string, and otherwise handle it as key to get it from the data
const resolveDatetime = (value: any, formData?: Record<string, any>) => {
    if (!value) return undefined;

    // Check if the value itself is a valid datetime
    if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) {
            return value;
        }

        // Otherwise treat it as a field name
        return formData?.[value];
    }

    return undefined;
};